import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ShieldCheck, Loader2, UserCog, UserPlus } from "lucide-react";
import { toast } from "sonner";
import { useSecurity } from "../context/SecurityContext";
import { makeMeAdmin, makeMeAdminOptions } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";

const PIN_RE = /^\d{4,6}$/;
const detail = (e, fb) => e?.response?.data?.detail || fb;

// Owner-only, shown while no Administrator exists: turn this profile into the Administrator, or create a separate one.
export const MakeMeAdminCard = () => {
  const qc = useQueryClient();
  const { member, isOwner, isAdmin, adminAccess, finishUnlock, refreshStatus } = useSecurity();
  const enabled = Boolean(isOwner && !isAdmin && member?.id && adminAccess?.reason === "bootstrap");
  const { data } = useQuery({ queryKey: ["make-me-admin"], queryFn: makeMeAdminOptions, enabled, retry: false });
  const [mode, setMode] = useState(null);
  const [name, setName] = useState("Administrator");
  const [pin, setPin] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  if (!enabled || !data?.available) return null;

  const done = async () => {
    qc.invalidateQueries({ queryKey: ["members"] });
    qc.invalidateQueries({ queryKey: ["make-me-admin"] });
    qc.invalidateQueries({ queryKey: ["activity"] });
    await refreshStatus();
  };
  const convert = async () => {
    setBusy(true);
    try {
      const r = await makeMeAdmin({ mode: "convert", email: email.trim() || undefined });
      finishUnlock(r.token, r.member);
      toast.success("You're now HearthVault's Administrator");
      await done();
    } catch (e) { toast.error(detail(e, "Couldn't convert your profile")); } finally { setBusy(false); }
  };
  const create = async () => {
    if (!PIN_RE.test(pin)) return toast.error("PIN must be 4–6 digits");
    setBusy(true);
    try {
      await makeMeAdmin({ mode: "create", name: name.trim() || "Administrator", pin, email: email.trim() || undefined });
      toast.success(`${name.trim() || "Administrator"} can now unlock as the Administrator`);
      setMode(null);
      await done();
    } catch (e) { toast.error(detail(e, "Couldn't create the Administrator")); } finally { setBusy(false); }
  };

  return (
    <div className="mt-4 rounded-xl border border-primary/30 bg-primary/5 p-4" data-testid="make-me-admin-card">
      <div className="flex items-start gap-3">
        <ShieldCheck className="h-4 w-4 text-primary mt-0.5 shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium">Appoint HearthVault's Administrator</div>
          <p className="text-xs text-muted-foreground mt-0.5">No Administrator exists yet, so every owner can open the Admin dashboard. Appoint one to make it Administrator-only. The Administrator can also sign in with an emailed code instead of a PIN.</p>
        </div>
      </div>
      {!mode && (
        <div className="mt-3 grid sm:grid-cols-2 gap-2">
          <button type="button" data-testid="make-me-admin-convert-btn" onClick={() => setMode("convert")} disabled={!data.can_convert} title={data.can_convert ? "" : "Add another owner first — the household must keep an owner"} className="rounded-lg border border-border bg-card p-3 text-left hover:border-primary disabled:opacity-50 disabled:cursor-not-allowed">
            <div className="flex items-center gap-1.5 text-sm font-medium"><UserCog className="h-3.5 w-3.5" /> Make my profile the Administrator</div>
            <div className="text-[11px] text-muted-foreground mt-0.5">{data.can_convert ? `Keeps your PIN. ${member?.name} becomes Administrator; another owner keeps the household.` : "Needs a second owner first — the household must keep one."}</div>
          </button>
          <button type="button" data-testid="make-me-admin-create-btn" onClick={() => setMode("create")} className="rounded-lg border border-border bg-card p-3 text-left hover:border-primary">
            <div className="flex items-center gap-1.5 text-sm font-medium"><UserPlus className="h-3.5 w-3.5" /> Create a separate Administrator profile</div>
            <div className="text-[11px] text-muted-foreground mt-0.5">Its own PIN on the lock screen. You stay the household owner too.</div>
          </button>
        </div>
      )}
      {mode === "convert" && (
        <div className="mt-3 space-y-2" data-testid="make-me-admin-convert-form">
          <Input data-testid="make-me-admin-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder={data.email || "Email for code sign-in (optional)"} className="bg-card h-9" />
          <div className="flex gap-2">
            <Button data-testid="make-me-admin-convert-confirm" size="sm" onClick={convert} disabled={busy} className="gap-1.5">{busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ShieldCheck className="h-3.5 w-3.5" />} Make me Administrator</Button>
            <Button size="sm" variant="ghost" onClick={() => setMode(null)}>Cancel</Button>
          </div>
        </div>
      )}
      {mode === "create" && (
        <div className="mt-3 space-y-2" data-testid="make-me-admin-create-form">
          <Input data-testid="make-me-admin-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Profile name" className="bg-card h-9" />
          <Input data-testid="make-me-admin-pin-input" inputMode="numeric" maxLength={6} value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))} placeholder="Administrator PIN (4–6 digits)" className="bg-card h-9" />
          <Input data-testid="make-me-admin-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email for code sign-in (optional)" className="bg-card h-9" />
          <div className="flex gap-2">
            <Button data-testid="make-me-admin-create-confirm" size="sm" onClick={create} disabled={busy} className="gap-1.5">{busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <UserPlus className="h-3.5 w-3.5" />} Create Administrator</Button>
            <Button size="sm" variant="ghost" onClick={() => setMode(null)}>Cancel</Button>
          </div>
        </div>
      )}
    </div>
  );
};
